/**
 * Real, OAuth-backed statutory report exporters for the Payroll Reports
 * screen (#23307). Each function calls one of the 5 tRPC procedures wired in
 * `apps/api/src/trpc/oauth_allowlist.ts` under `payroll:employer:read`:
 *
 *   - `epf.getFormC`                — Sri Lanka monthly EPF return (CSV)
 *   - `etf.getFormII`               — Sri Lanka monthly ETF return (CSV)
 *   - `etf.getFormR4`               — Sri Lanka annual ETF member statement (CSV)
 *   - `apit.getMonthlyReturn`       — monthly APIT/PAYE remittance (JSON→CSV here)
 *   - `apit.getAnnualReconciliation`— annual APIT reconciliation (JSON→CSV here)
 *
 * The EPF and ETF procedures already return a preformatted `csv_content`;
 * APIT returns structured JSON, so this module also owns the CSV formatting
 * for the two APIT exports so the screen has a single uniform "here is your
 * CSV" contract to consume regardless of procedure shape.
 *
 * Never throws — every function resolves a discriminated
 * `{ ok: true, csv_content, filename } | { ok: false, reason }`. Mirrors
 * `oauth_pay_run_post_actions.ts`'s never-throws contract and its use of
 * `isPayrollAddonRequiredError` (though these 5 procedures are NOT gated by
 * the payroll add-on — they gate on `requireReport` — the classification is
 * kept for shape parity should a future entity-level entitlement gate be
 * added).
 */
import { getTrpcClient, isPayrollAddonRequiredError } from '../trpc.js';
import type { PayrollReportId } from '../../data/types.js';

export type StatutoryReportResult =
  | { ok: true; csv_content: string; filename: string; mime_type: 'text/csv' }
  | { ok: false; reason: 'entitlement_required' }
  | { ok: false; reason: 'not_available' }
  | { ok: false; reason: 'error'; message: string };

const MIME = 'text/csv' as const;

function classifyError(err: unknown): StatutoryReportResult {
  if (isPayrollAddonRequiredError(err)) {
    return { ok: false, reason: 'entitlement_required' };
  }
  // `requireReport` returns NOT_FOUND for non-LK entities to avoid leaking
  // report existence (security-rules.md). Surface a dedicated `not_available`
  // reason so the screen renders a clear "not available for this entity"
  // message rather than the generic error copy.
  const code = (err as { data?: { code?: string } } | null)?.data?.code;
  if (code === 'NOT_FOUND') {
    return { ok: false, reason: 'not_available' };
  }
  return {
    ok: false,
    reason: 'error',
    message:
      err instanceof Error ? err.message : 'unknown error generating report',
  };
}

/** Escape a CSV field per RFC 4180 (double any embedded quotes, wrap when needed). */
function csvField(value: string | number): string {
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/** Format a minor-units integer as a two-decimal number for CSV output. */
function fromMinorUnits(cents: number): string {
  return (cents / 100).toFixed(2);
}

// ── EPF Form C ────────────────────────────────────────────────────────────

export async function fetchEpfFormC(input: {
  firm_id: string;
  entity_id: string;
  periodStart: string;
  periodEnd: string;
}): Promise<StatutoryReportResult> {
  try {
    const client = getTrpcClient();
    const result = await client.epf.getFormC.query(input);
    return {
      ok: true,
      csv_content: result.csv_content,
      filename: result.filename,
      mime_type: MIME,
    };
  } catch (err) {
    console.error('[wagepro-web] epf.getFormC failed', err);
    return classifyError(err);
  }
}

// ── ETF Form II ───────────────────────────────────────────────────────────

export async function fetchEtfFormII(input: {
  firm_id: string;
  entity_id: string;
  periodStart: string;
  periodEnd: string;
}): Promise<StatutoryReportResult> {
  try {
    const client = getTrpcClient();
    const result = await client.etf.getFormII.query(input);
    return {
      ok: true,
      csv_content: result.csv_content,
      filename: result.filename,
      mime_type: MIME,
    };
  } catch (err) {
    console.error('[wagepro-web] etf.getFormII failed', err);
    return classifyError(err);
  }
}

// ── ETF Form R4 ───────────────────────────────────────────────────────────

export async function fetchEtfFormR4(input: {
  firm_id: string;
  entity_id: string;
  year: number;
}): Promise<StatutoryReportResult> {
  try {
    const client = getTrpcClient();
    const result = await client.etf.getFormR4.query(input);
    return {
      ok: true,
      csv_content: result.csv_content,
      filename: result.filename,
      mime_type: MIME,
    };
  } catch (err) {
    console.error('[wagepro-web] etf.getFormR4 failed', err);
    return classifyError(err);
  }
}

// ── APIT Monthly ──────────────────────────────────────────────────────────

/**
 * `apit.getMonthlyReturn` returns structured JSON (not preformatted CSV like
 * EPF/ETF). Convert to CSV here so the screen has one uniform shape to
 * download. Amounts formatted from minor units to two-decimal strings per
 * money-rules.md.
 */
export async function fetchApitMonthly(input: {
  firm_id: string;
  entity_id: string;
  periodStart: string;
  periodEnd: string;
}): Promise<StatutoryReportResult> {
  try {
    const client = getTrpcClient();
    const result = await client.apit.getMonthlyReturn.query(input);
    const header = 'Employee,Gross Pay,APIT Deducted';
    const body = result.rows
      .map(
        (r) =>
          `${csvField(r.employee_name)},${fromMinorUnits(r.gross_cents)},${fromMinorUnits(r.apit_cents)}`,
      )
      .join('\r\n');
    const totals = `TOTAL,${fromMinorUnits(result.total_gross_cents)},${fromMinorUnits(result.total_apit_cents)}`;
    const csv_content = [header, body, totals].filter((x) => x).join('\r\n');
    return {
      ok: true,
      csv_content,
      filename: `apit_monthly_${result.period}.csv`,
      mime_type: MIME,
    };
  } catch (err) {
    console.error('[wagepro-web] apit.getMonthlyReturn failed', err);
    return classifyError(err);
  }
}

// ── APIT Annual ───────────────────────────────────────────────────────────

/**
 * `apit.getAnnualReconciliation` returns structured JSON — same rationale as
 * `fetchApitMonthly` above for converting to CSV here rather than at the
 * screen. `variance_cents` may be negative (under-withheld) and its sign is
 * preserved in the CSV output (`fromMinorUnits(-123)` renders `-1.23`).
 */
export async function fetchApitAnnual(input: {
  firm_id: string;
  entity_id: string;
  taxYear: number;
}): Promise<StatutoryReportResult> {
  try {
    const client = getTrpcClient();
    const result = await client.apit.getAnnualReconciliation.query(input);
    const header =
      'Employee,Annual Gross,APIT Deducted,APIT Liability,Variance';
    const body = result.rows
      .map(
        (r) =>
          `${csvField(r.employee_name)},${fromMinorUnits(r.annual_gross_cents)},${fromMinorUnits(r.deducted_cents)},${fromMinorUnits(r.liability_cents)},${fromMinorUnits(r.variance_cents)}`,
      )
      .join('\r\n');
    const totals = `TOTAL,${fromMinorUnits(result.total_gross_cents)},${fromMinorUnits(result.total_deducted_cents)},${fromMinorUnits(result.total_liability_cents)},${fromMinorUnits(result.total_variance_cents)}`;
    const csv_content = [header, body, totals].filter((x) => x).join('\r\n');
    return {
      ok: true,
      csv_content,
      filename: `apit_annual_${String(result.taxYear)}.csv`,
      mime_type: MIME,
    };
  } catch (err) {
    console.error('[wagepro-web] apit.getAnnualReconciliation failed', err);
    return classifyError(err);
  }
}

// ── Report id → fetcher dispatch ──────────────────────────────────────────

/**
 * Discriminator for a report's period-input shape. Kept alongside the
 * fetchers so the screen has one place to look up "what does this report
 * need" — an EPF/ETF Form II / APIT monthly needs an ISO month (from which
 * `periodStart`/`periodEnd` are derived); ETF Form R4 / APIT annual need a
 * calendar year.
 */
export type StatutoryReportKind = 'monthly' | 'annual';

export const STATUTORY_REPORT_KIND: Record<
  PayrollReportId,
  StatutoryReportKind
> = {
  epf_form_c: 'monthly',
  etf_form_ii: 'monthly',
  etf_form_r4: 'annual',
  apit_monthly: 'monthly',
  apit_annual: 'annual',
};

/**
 * Given an ISO month string (`YYYY-MM`), returns
 * `{ periodStart: 'YYYY-MM-01', periodEnd: 'YYYY-MM-<last-day>' }`.
 * `Date.UTC(y, m, 0)` returns the last day of month `m-1` (JavaScript
 * quirk: month is 1-indexed here because we pass `m` not `m-1`), which is
 * exactly the last day of the requested month.
 */
export function monthToPeriodRange(month: string): {
  periodStart: string;
  periodEnd: string;
} {
  const [yStr, mStr] = month.split('-');
  const y = parseInt(yStr, 10);
  const m = parseInt(mStr, 10);
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const periodStart = `${yStr}-${mStr}-01`;
  const periodEnd = `${yStr}-${mStr}-${String(lastDay).padStart(2, '0')}`;
  return { periodStart, periodEnd };
}

export async function fetchStatutoryReport(
  reportId: PayrollReportId,
  ctx: { firm_id: string; entity_id: string },
  period: { month?: string; year?: number },
): Promise<StatutoryReportResult> {
  if (reportId === 'etf_form_r4') {
    if (period.year === undefined) {
      return { ok: false, reason: 'error', message: 'missing year' };
    }
    return fetchEtfFormR4({ ...ctx, year: period.year });
  }
  if (reportId === 'apit_annual') {
    if (period.year === undefined) {
      return { ok: false, reason: 'error', message: 'missing year' };
    }
    return fetchApitAnnual({ ...ctx, taxYear: period.year });
  }
  if (period.month === undefined) {
    return { ok: false, reason: 'error', message: 'missing month' };
  }
  const { periodStart, periodEnd } = monthToPeriodRange(period.month);
  if (reportId === 'epf_form_c') {
    return fetchEpfFormC({ ...ctx, periodStart, periodEnd });
  }
  if (reportId === 'etf_form_ii') {
    return fetchEtfFormII({ ...ctx, periodStart, periodEnd });
  }
  return fetchApitMonthly({ ...ctx, periodStart, periodEnd });
}
